import first from 'lodash/first';
import React, { Component } from 'react';
import { UserCheck, Users } from 'react-feather';
import { connect } from 'react-redux';
import {
  requestAttendees,
  updateFilters,
} from 'actions/inPersonTraining/eventOverview/attendees';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { routes } from 'constants/config';
import { Card } from 'ui';
import Select from 'components/shared/select';
import FeatureFlag from 'components/shared/FeatureFlag';
import request from 'utils/request_ajax';
import AttendanceTableMultipleColumn from './AttendanceTableMultipleColumn';
import MultiSessionAttendanceTable from './MultiSessionAttendanceTable';
import SearchBox from './SearchBox';
import SearchResults from './SearchResults';
import { getStatusText } from '../helpers.js';
import ZoomAttendanceTrackingInfo from './ZoomAttendanceTrackingInfo';

class Attendees extends Component {
  state = {
    query: '',
    isFetchingAttendance: false,
    attendanceFetchError: null,
  };

  constructor(props) {
    super(props);
    this.handleRSVPFilterChange = this.handleRSVPFilterChange.bind(this);
    this.handleAttendanceFilterChange =
      this.handleAttendanceFilterChange.bind(this);
  }

  componentDidMount() {
    this.props.requestAttendees(this.props.currentSessionId);
  }

  componentDidUpdate(prevProps, prevState) {
    const prevQuery = prevState.query.trim();
    const query = this.state.query.trim();
    /**
     * reloading attendance data after clearing search in case records were
     * modified during the search phase
     */
    if (prevQuery !== query && query === '') {
      this.props.requestAttendees(this.props.currentSessionId);
    }
  }

  handleRSVPFilterChange(e) {
    const rsvpStatus = e.target.value;
    this.props.updateFilters({ rsvpStatus }, this.props.currentSessionId);
  }

  handleAttendanceFilterChange(e) {
    const attendanceStatus = e.target.value;
    this.props.updateFilters({ attendanceStatus }, this.props.currentSessionId);
  }

  /**
   * Update the component only when it finishes loading.
   * This prevents hiding certain parts such the status filters as soon as we
   * toggle props.isSearching and defers that update until the component
   * finishes loading.
   */
  shouldComponentUpdate(nextProps) {
    return !nextProps.loading;
  }

  handleSearch = query => {
    this.setState({
      query,
    });
  };

  handleTakeAttendance = () => {
    const { event, session } = this.props;
    this.setState({ isFetchingAttendance: true, attendanceFetchError: null });

    request
      .postJSON(
        routes.IN_PERSON_TRAINING.SESSION_TAKE_ATTENDANCE(event.id, session.id),
        {},
      )
      .then(res => {
        this.setState({ isFetchingAttendance: false });
        if (res.success) {
          window.location = routes.IN_PERSON_TRAINING.EVENT(event.id);
        }
      })
      .catch(error => {
        const { responseJSON, responseText } = error;
        this.setState({
          isFetchingAttendance: false,
          attendanceFetchError: responseJSON || responseText,
        });
      });
  };

  render() {
    const { isFetchingAttendance, attendanceFetchError } = this.state;
    const {
      attendanceStatus,
      currentSessionId,
      currentUser,
      event,
      isArchived,
      loading,
      rsvpStatus,
      session,
      sessions,
      totalAttendees,
    } = this.props;

    const isMultiSession = sessions.length > 1;
    const { video_meeting: videoMeeting } = session;
    const isSearching = !!this.state.query.trim();
    const hasAttendances = totalAttendees !== 0;
    const hasPastAttendances = hasAttendances && !session.upcoming;
    return (
      <Card className="AttendanceTable Animated Animated--fadeIn">
        <Card.Content padding="large">
          <div className="d-f jc-sb mb-16">
            <Text variant={TEXT_VARIANTS.SUBHEADER_BOLD}>
              {t('in_person_training.event_overview.attendances.guests')}
            </Text>
            {videoMeeting && videoMeeting.provider === 'other' && (
              <div className="AttendanceBeta">
                <span
                  className="ml-8 fz-12 radio-tooltip"
                  data-toggle="tooltip"
                  data-placement="top"
                  title={t(
                    'in_person_training.event_overview.session.other.attendance_tracking_tooltip',
                  )}
                >
                  <UserCheck className="AttendanceUserIcon" />
                  <Text
                    variant={TEXT_VARIANTS.BODY_LIGHT}
                    className="pr-8 pl-8 fz-14"
                  >
                    {t(
                      'in_person_training.event_overview.session.zoom.attendance_tracking_beta',
                    )}
                  </Text>
                </span>
              </div>
            )}
            {!loading && videoMeeting && videoMeeting.provider === 'zoom' && (
              <ZoomAttendanceTrackingInfo
                currentUser={currentUser}
                session={session}
                totalInvitees={totalAttendees}
                onTakeAttendance={this.handleTakeAttendance}
                isFetchingAttendance={isFetchingAttendance}
                attendanceFetchError={attendanceFetchError}
              />
            )}
          </div>
          <div className="d-f fd-r ai-c">
            <SearchBox onSubmit={this.handleSearch} query={this.state.query} />
            {hasAttendances && (!isMultiSession || currentSessionId) && (
              <div className="AttendanceFilters">
                <div className="AttendanceFilterSelectContainer">
                  <Select
                    onChange={this.handleRSVPFilterChange}
                    value={rsvpStatus}
                  >
                    <option value="" key="">
                      {t(
                        'in_person_training.event_overview.attendances.all_rsvps',
                      )}
                    </option>
                    <option value="awaiting_reply" key="awaiting_reply">
                      {getStatusText('awaiting_reply')}
                    </option>
                    <option value="will_attend" key="will_attend">
                      {getStatusText('will_attend')}
                    </option>
                    <option value="will_not_attend" key="will_not_attend">
                      {getStatusText('will_not_attend')}
                    </option>
                  </Select>
                </div>
                {hasPastAttendances && !isMultiSession && (
                  <div className="AttendanceFilterSelectContainer">
                    <Select
                      onChange={this.handleAttendanceFilterChange}
                      value={attendanceStatus}
                    >
                      <option value="" key="">
                        {t(
                          'in_person_training.event_overview.attendances.all_attendances',
                        )}
                      </option>
                      <option value="did_attend" key="did_attend">
                        {getStatusText('did_attend')}
                      </option>
                      <option value="did_not_attend" key="did_not_attend">
                        {getStatusText('did_not_attend')}
                      </option>
                      <option value="unconfirmed" key="unconfirmed">
                        {getStatusText('unconfirmed', false, true, true)}
                      </option>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>
          {event.next_session && (
            <div className="mt-4">
              <Text variant={TEXT_VARIANTS.BODY}>
                {t(
                  'in_person_training.event_overview.attendance.invite_group_or',
                )}{' '}
              </Text>

              <Text variant={TEXT_VARIANTS.BODY_LINK}>
                <a
                  href={routes.IN_PERSON_TRAINING.SESSION_INVITATIONS(
                    event.id,
                    event.next_session.id,
                  )}
                >
                  {t(
                    'in_person_training.event_overview.attendance.invite_group_button',
                  )}
                </a>
              </Text>
            </div>
          )}
        </Card.Content>
        <FeatureFlag features="events_in_development">
          {!isSearching && hasAttendances && isMultiSession && (
            <MultiSessionAttendanceTable
              currentSessionId={currentSessionId}
              isMultiSession={isMultiSession}
              rsvpType={event.rsvp_type}
              sessions={sessions}
            />
          )}
        </FeatureFlag>
        {!isSearching && hasAttendances && !isMultiSession && (
          <AttendanceTableMultipleColumn
            sessions={sessions}
            isMultiSession={isMultiSession}
          />
        )}
        {isSearching && (
          <SearchResults
            currentSessionId={currentSessionId}
            sessions={sessions}
            isMultiSession={isMultiSession}
            query={this.state.query}
          />
        )}
        {!isArchived && !isSearching && !hasAttendances && event.next_session && (
          <div className="fz-bigger llui-text llui-text-body-light px-24 pb-40 ta-c">
            <Users className="AttendanceUsersIcon" />
            <div>
              <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                {t(
                  'in_person_training.event_overview.attendance.no_attendees.no_guests',
                )}
              </Text>
            </div>
            <div>
              <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                {t(
                  'in_person_training.event_overview.attendance.no_attendees.no_guests_subtext',
                )}
              </Text>
            </div>
          </div>
        )}
      </Card>
    );
  }
}

const mapStateToProps = state => ({
  event: state.event,
  currentUser: state.meta.currentUser,
  session: first(state.sessions),
  sessions: state.sessions,
  totalAttendees: state.attendees.attendances.totalAttendees,
  loading: state.attendees.loading,
  rsvpStatus: state.attendees.filters.rsvpStatus,
  attendanceStatus: state.attendees.filters.attendanceStatus,
  isArchived: state.event.is_archived,
});

const mapDispatchToProps = {
  requestAttendees,
  updateFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(Attendees);
